import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useGlobalContext } from "../../contexts/GlobalContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { X } from "lucide-react";

export function GlobalContextSelector() {
  const location = useLocation();
  const { isViewer, isEntityAdmin, isClientAdmin, isDPO, isSuperAdmin } = useAuth();
  const {
    selectedClientId,
    selectedEntityId,
    selectedClient,
    clients,
    entities,
    departments,
    loadingEntities,
    loadingDepartments,
    selectedDepartmentId,
    setSelectedClientId,
    setSelectedEntityId,
    setSelectedDepartmentId,
    clearContext,
    isClientLocked,
    isEntityLocked,
  } = useGlobalContext();

  // Determine if current module supports department filtering
  const supportsDepartments = ["ropa", "risk", "dpia-intake", "prescreen", "questionnaire"].some(
    (module) => location.pathname.includes(`/compliance/${module}`) || location.pathname.includes(`/risk/${module}`),
  );

  // Determine if selector should be shown at all
  if (isViewer || isEntityAdmin) {
    return null;
  }

  // For Client Admin with non-group client type, don't show selector
  if (isClientAdmin && selectedClient?.client_type !== "group") {
    return null;
  }

  // For Client Admin with group type, show entity dropdown and optionally department dropdown
  if (isClientAdmin && selectedClient?.client_type === "group") {
    return (
      <div className="flex items-center gap-3 px-6 py-2 border-b bg-muted/30 sticky top-0 z-20 backdrop-blur-sm">
        <span className="text-sm text-muted-foreground">Entity:</span>
        <Select
          value={selectedEntityId || "__all__"}
          onValueChange={(val) => setSelectedEntityId(val === "__all__" ? null : val)}
          disabled={isEntityLocked || loadingEntities}
        >
          <SelectTrigger className="w-[200px] h-8 text-sm">
            <SelectValue placeholder={loadingEntities ? "Loading..." : "All Entities"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Entities</SelectItem>
            {entities.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.entity_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {supportsDepartments && (
          <>
            {departments.length === 0 ? (
              <span className="text-sm text-muted-foreground italic">No departments configured for this entity</span>
            ) : (
              <>
                <span className="text-sm text-muted-foreground">Department:</span>
                <Select
                  value={selectedDepartmentId || "__all__"}
                  onValueChange={(val) => setSelectedDepartmentId(val === "__all__" ? null : val)}
                  disabled={loadingDepartments || !selectedEntityId}
                >
                  <SelectTrigger className="w-[200px] h-8 text-sm">
                    <SelectValue placeholder={loadingDepartments ? "Loading..." : "All Departments"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Departments</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.name}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  // For DPO / Super Admin, show client, entity, and optionally department dropdowns
  if (isDPO || isSuperAdmin) {
    return (
      <div className="flex items-center gap-3 px-6 py-2 border-b bg-muted/30 sticky top-0 z-20 backdrop-blur-sm">
        <span className="text-sm text-muted-foreground">Client:</span>
        <Select
          value={selectedClientId || "__all__"}
          onValueChange={(val) => setSelectedClientId(val === "__all__" ? null : val)}
          disabled={isClientLocked}
        >
          <SelectTrigger className="w-[200px] h-8 text-sm">
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.client_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-sm text-muted-foreground">Entity:</span>
        <Select
          value={selectedEntityId || "__all__"}
          onValueChange={(val) => setSelectedEntityId(val === "__all__" ? null : val)}
          disabled={isEntityLocked || loadingEntities || !selectedClientId}
        >
          <SelectTrigger className="w-[200px] h-8 text-sm">
            <SelectValue placeholder={loadingEntities ? "Loading..." : "All Entities"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Entities</SelectItem>
            {entities.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.entity_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {supportsDepartments && (
          <>
            {departments.length === 0 ? (
              <span className="text-sm text-muted-foreground italic">No departments configured for this entity</span>
            ) : (
              <>
                <span className="text-sm text-muted-foreground">Department:</span>
                <Select
                  value={selectedDepartmentId || "__all__"}
                  onValueChange={(val) => setSelectedDepartmentId(val === "__all__" ? null : val)}
                  disabled={loadingDepartments || !selectedEntityId}
                >
                  <SelectTrigger className="w-[200px] h-8 text-sm">
                    <SelectValue placeholder={loadingDepartments ? "Loading..." : "All Departments"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Departments</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.name}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </>
        )}

        {(selectedClientId || selectedEntityId) && !isClientLocked && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearContext}
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        )}
      </div>
    );
  }

  // Default: no selector shown
  return null;
}
